(() => {
  'use strict'

  const FORM_SELECTOR = '[data-newsletter-custom]'
  const MIN_FILL_TIME_MS = 2000
  const KLAVIYO_CLIENT_API = 'https://a.klaviyo.com/client'
  const KLAVIYO_REVISION = '2025-07-15'

  const notify = (message, type) => {
    const notification = window.bodyElement && window.bodyElement.notification
    if (notification && typeof notification.show === 'function') {
      notification.show(message, type)
    }
  }

  const setFieldError = (input, hasError) => {
    const field = input.closest('.newsletter-custom__field')
    if (field) field.classList.toggle('newsletter-custom__field--invalid', hasError)

    // Les cases a cocher ne sont pas dans un .newsletter-custom__field :
    // sans ca, un consentement oublie n'affiche aucune erreur.
    const checkbox = input.closest('.newsletter-custom__checkbox')
    if (checkbox) checkbox.classList.toggle('newsletter-custom__checkbox--invalid', hasError)

    input.setAttribute('aria-invalid', hasError ? 'true' : 'false')
  }

  // Klaviyo rejette tout numero qui n'est pas en E.164, autant normaliser ici.
  const normalizePhone = (value, defaultCountryCode = '33') => {
    const trimmed = value.trim()
    if (!trimmed) return ''

    const digits = trimmed.replace(/[^\d+]/g, '')
    if (digits.startsWith('+')) return digits
    if (digits.startsWith('00')) return `+${digits.slice(2)}`
    if (digits.startsWith('0')) return `+${defaultCountryCode}${digits.slice(1)}`
    return `+${digits}`
  }

  const validate = (form) => {
    const errors = []

    form.querySelectorAll('input[required], textarea[required]').forEach((input) => {
      const isCheckbox = input.type === 'checkbox'
      const empty = isCheckbox ? !input.checked : !input.value.trim()

      setFieldError(input, empty)
      if (empty) errors.push(input)
    })

    const email = form.querySelector('[name="email"]')
    if (email && email.value.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.value.trim())) {
      setFieldError(email, true)
      errors.push(email)
    }

    const phone = form.querySelector('[name="phone_number"]')
    if (phone && phone.value.trim()) {
      const normalized = normalizePhone(phone.value)
      const valid = /^\+[1-9]\d{7,14}$/.test(normalized)
      setFieldError(phone, !valid)
      if (!valid) errors.push(phone)
    }

    return errors
  }

  const buildPayload = (form) => {
    const data = new FormData(form)
    const phone = (data.get('phone_number') || '').trim()

    return {
      email: (data.get('email') || '').trim(),
      first_name: (data.get('first_name') || '').trim(),
      last_name: (data.get('last_name') || '').trim(),
      phone_number: phone ? normalizePhone(phone) : '',
      message: (data.get('message') || '').trim(),
      email_consent: data.get('email_consent') === 'true',
      sms_consent: data.get('sms_consent') === 'true',
      // Toujours un booleen : un "non" explicite doit rester distinguable
      // d'une question jamais posee, sinon le segment devient faux.
      circuit_interest: form.querySelector('[name="circuit_interest"]') ? data.get('circuit_interest') === 'true' : null,
      company: (data.get('company') || '').trim(),
      source: form.dataset.source || '',
      page_url: window.location.href,
      elapsed_ms: Date.now() - Number(form.dataset.renderedAt || Date.now()),
    }
  }

  const buildProperties = (payload) => {
    const properties = {
      signup_source: payload.source,
      signup_page: payload.page_url,
    }

    if (payload.circuit_interest !== null) properties.circuit_interest = payload.circuit_interest

    // Miroir du dernier message sur le profil, pour le lire sans ouvrir
    // l'historique. Volontairement absent si vide : ne jamais effacer le
    // message precedent avec une chaine vide.
    if (payload.message) properties.last_form_message = payload.message

    return properties
  }

  const klaviyoPost = async (form, path, body) => {
    const url = `${KLAVIYO_CLIENT_API}${path}?company_id=${encodeURIComponent(form.dataset.companyId)}`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const detail = await response.text().catch(() => '')
      throw new Error(`Klaviyo ${path} -> ${response.status} ${detail.slice(0, 300)}`)
    }
  }

  // Inscription a la liste. Tout le monde y entre, quelle que soit la reponse
  // a la case circuit : le tri se fait ensuite par segment.
  const subscribe = (form, payload, properties) => {
    const subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } }
    if (payload.sms_consent && payload.phone_number) {
      subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } }
    }

    const attributes = {
      email: payload.email,
      properties,
      subscriptions,
    }

    if (payload.phone_number) attributes.phone_number = payload.phone_number
    if (payload.first_name) attributes.first_name = payload.first_name
    if (payload.last_name) attributes.last_name = payload.last_name

    return klaviyoPost(form, '/subscriptions/', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: payload.source,
          profile: { data: { type: 'profile', attributes } },
        },
        relationships: {
          list: { data: { type: 'list', id: form.dataset.listId } },
        },
      },
    })
  }

  // Evenement horodate : conserve l'historique (message libre, reponse circuit
  // du jour) la ou une propriete de profil serait ecrasee a chaque envoi.
  const trackEvent = (form, payload, properties) => {
    const metric = form.dataset.metric
    if (!metric) return Promise.resolve()

    return klaviyoPost(form, '/events/', {
      data: {
        type: 'event',
        attributes: {
          properties: {
            ...properties,
            sms_consent: payload.sms_consent,
            message: payload.message || null,
          },
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: { email: payload.email, properties } } },
        },
      },
    })
  }

  const setStatus = (form, message, state) => {
    const status = form.querySelector('[data-newsletter-status]')
    if (!status) return
    status.textContent = message
    status.dataset.state = state
  }

  const setLoading = (form, isLoading) => {
    form.dataset.loading = isLoading ? 'true' : 'false'
    const button = form.querySelector('.newsletter-custom__submit')
    if (button) button.disabled = isLoading
  }

  const submit = async (form) => {
    const errorMessage = form.dataset.errorMessage || 'Une erreur est survenue.'

    const invalid = validate(form)
    if (invalid.length > 0) {
      invalid[0].focus()
      setStatus(form, 'Merci de verifier les champs en rouge.', 'error')
      return
    }

    const payload = buildPayload(form)

    // Un humain ne remplit pas ce formulaire en moins de deux secondes.
    if (payload.company || payload.elapsed_ms < MIN_FILL_TIME_MS) {
      setStatus(form, form.dataset.successMessage || 'Merci !', 'success')
      form.reset()
      return
    }

    setLoading(form, true)
    setStatus(form, '', '')

    try {
      const properties = buildProperties(payload)

      // L'inscription doit aboutir ; l'evenement est du confort, son echec
      // ne doit pas faire croire au visiteur que rien n'a ete enregistre.
      await subscribe(form, payload, properties)
      trackEvent(form, payload, properties).catch((error) =>
        console.warn('[newsletter-custom] event', error)
      )

      const success = form.dataset.successMessage || 'Merci !'
      setStatus(form, success, 'success')
      notify(success, 'success')
      form.reset()
      form.dataset.renderedAt = String(Date.now())
    } catch (error) {
      console.error('[newsletter-custom]', error)
      setStatus(form, errorMessage, 'error')
      notify(errorMessage, 'warning')
    } finally {
      setLoading(form, false)
    }
  }

  const init = (form) => {
    if (form.dataset.initialized === 'true') return
    form.dataset.initialized = 'true'
    form.dataset.renderedAt = String(Date.now())

    form.addEventListener('submit', (event) => {
      event.preventDefault()
      submit(form)
    })

    form.querySelectorAll('input, textarea').forEach((input) => {
      input.addEventListener('input', () => setFieldError(input, false))
      input.addEventListener('change', () => setFieldError(input, false))
    })
  }

  const initAll = (root = document) => {
    root.querySelectorAll(FORM_SELECTOR).forEach(init)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll())
  } else {
    initAll()
  }

  // Rechargement d'une section depuis l'editeur de theme.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target))
})()
